import { ImageDropzone } from '@/components/ImageDropzone'
import { laravelGraphqlEndpoint } from '@/globals/globals'
import { UserContext } from '@/lib/UserContext'
import { Image } from '@/types/image'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import request, { gql } from 'graphql-request'
import React, { useContext } from 'react'

interface Values {
  name: string
  email: string
  message: string
  upload?: string
  orderId?: string
}

type ContactFormProps = {
  formSlug: string
}

const forms = new Map<string, { [key: string]: string | Set<string> }>([
  [
    'merch',
    {
      fields: new Set(['name', 'email', 'orderId', 'upload', 'message']),
      sendTo: 'debossgarage@gmail.com',
      title: 'Merch Support',
    },
  ],
  [
    'website',
    {
      fields: new Set(['name', 'email', 'message']),
      sendTo: 'deboss.website@gmail.com',
      title: 'Website Support',
    },
  ],
  [
    'business',
    {
      fields: new Set(['name', 'email', 'message']),
      sendTo: 'info@debossgarage.com',
      title: 'Business Inquiry',
    },
  ],
])

const hasField = ({
  formSlug,
  fieldName,
}: {
  formSlug: string
  fieldName: string
}) => {
  const formFields = forms.get(formSlug)?.fields
  return typeof formFields !== 'string' && !!formFields?.has(fieldName)
}

export const ContactForm = (props: ContactFormProps) => {
  const [submitted, setSubmitted] = React.useState(false)
  const [upload, setUpload] = React.useState<Image[]>()

  // Get user
  const userData = useContext(UserContext)
  const user = userData()
  const toast = useToast()

  const form = forms.get(props.formSlug)

  const submit = async (values: Values) => {
    if (!form?.sendTo) {
      toast({
        title: 'Error',
        description: 'There was an error sending your message.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw new Error('No sendTo email address found')
    }

    await request(
      laravelGraphqlEndpoint,
      gql`
        mutation Support(
            $email: String!, 
            $message: String!
            $name: String!, 
            $orderId: String
            $upload: String
            ) {
          createSupportSubmission(
            email: $email
            message: $message
            name: $name
            orderId: $orderId
            sendTo: "${form.sendTo}"
            type: "${props.formSlug}"
            upload: $upload
          ) {
            id
          }
        }
      `,
      {
        ...values,
        orderId: values.orderId !== '' ? values.orderId : null,
        upload: upload ? JSON.stringify(upload.map(image => image.path)) : null,
      }
    ).then(() => {
      setSubmitted(true)
    })
  }

  if (submitted) {
    return (
      <Box p={8}>
        <Heading textAlign='center' fontSize='xl'>
          Thank you for your message. <br /> We will get back to you shortly.
        </Heading>
      </Box>
    )
  }

  const handleNewImages = (images: Image[]) => {
    if (upload) setUpload([...upload, ...images])
    if (!upload) setUpload([...images])
  }

  return (
    <Box py={4}>
      <Formik
        initialValues={{
          email: user?.email || '',
          message: '',
          name: user?.name || '',
          orderId: '',
        }}
        onSubmit={(
          values: Values,
          { setSubmitting }: FormikHelpers<Values>
        ) => {
          submit(values)
          setSubmitting(false)
        }}>
        <Form>
          <Stack spacing={4}>
            <Heading>{form?.title || 'Send a message'}</Heading>

            <Field name='name'>
              {({ field, form }: any) => (
                <FormControl isInvalid={form.errors.name}>
                  <FormLabel htmlFor='name'>Your Name</FormLabel>
                  <Input {...field} id='name' required />
                  <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name='email'>
              {({ field, form }: any) => (
                <FormControl isInvalid={form.errors.email}>
                  <FormLabel htmlFor='email'>Email Address</FormLabel>
                  <Input {...field} id='email' type='email' required />
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            {hasField({ formSlug: props.formSlug, fieldName: 'orderId' }) && (
              <Field name='orderId'>
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.email}>
                    <FormLabel htmlFor='orderId'>Order ID</FormLabel>
                    <Input {...field} id='orderId' type='orderId' />
                    <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            )}

            {hasField({ formSlug: props.formSlug, fieldName: 'upload' }) && (
              <>
                <Text>Upload images of the product issue</Text>
                {upload &&
                  upload.map(file => (
                    <img key={file.uuid} src={file.preview} alt='' />
                  ))}
                <ImageDropzone
                  images={upload ? [upload] : []}
                  setImages={handleNewImages}
                />
              </>
            )}

            <Field name='message'>
              {({ field, form }: any) => (
                <FormControl isInvalid={form.errors.message}>
                  <FormLabel htmlFor='message'>Message</FormLabel>
                  <Textarea {...field} id='message' required height={200} />
                  <FormErrorMessage>{form.errors.message}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Button type='submit' colorScheme='red'>
              Send Message
            </Button>
          </Stack>
        </Form>
      </Formik>
    </Box>
  )
}
